"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import { type LegalDocument } from "./types";
import { scrollToId } from "@/lib/utils";
import { HeroGlow } from "@/components/ui";

export type { LegalSection, LegalDocument } from "./types";
export { LegalP, LegalUL, LegalLI, LegalOL, LegalStrong, LegalH3 } from "./parts";

type Props = {
  doc: LegalDocument;
};

export default function LegalPostSection({ doc }: Props) {
  const [activeId, setActiveId] = useState<string>(doc.sections[0]?.id ?? "");

  useEffect(() => {
    const OFFSET = 120;
    const update = () => {
      let current = doc.sections[0]?.id ?? "";
      for (const { id } of doc.sections) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= OFFSET) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [doc.sections]);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative isolate min-h-screen overflow-y-clip"
      style={{ background: "var(--background)" }}
    >
      {/* These pages have no hero section — HeroGlow bounds itself to a band at
          the top of the document, so it lands where it does everywhere else. */}
      <HeroGlow align="left" behind />

      {/* ── Body ── */}
      <div className="relative mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6 lg:px-8 lg:pt-32 lg:pb-16">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16 xl:gap-20">
          {/* ── Sidebar ── */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 flex max-h-[calc(100vh-6rem)] flex-col">
              <div
                className="mb-6 shrink-0 pb-5"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <Link
                  href="/legal"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  style={{ color: "var(--foreground)" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--foreground)")
                  }
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="h-4 w-4 shrink-0"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 8H3m0 0l4-4M3 8l4 4"
                    />
                  </svg>
                  Legal
                </Link>
                <p className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                  Updated {doc.lastUpdated}
                </p>
              </div>

              <nav aria-label="Table of contents" className="flex min-h-0 flex-1 flex-col">
                <p
                  className="mb-3 shrink-0 text-[11px] font-semibold tracking-widest uppercase"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  On this page
                </p>
                <ul className="toc-scroll min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
                  {doc.sections.map((s) => {
                    const isActive = activeId === s.id;
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => scrollToId(s.id)}
                          className="w-full rounded-md px-3 py-1.5 text-left text-sm leading-snug transition-all duration-150"
                          style={{
                            color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                            background: isActive ? "rgba(247,247,248,0.07)" : "transparent",
                            fontWeight: isActive ? 500 : 400,
                            borderLeft: isActive
                              ? "2px solid var(--foreground)"
                              : "2px solid transparent",
                          }}
                        >
                          {s.heading}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </aside>

          {/* ── Main article ── */}
          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="min-w-0"
          >
            <header className="mb-10 pb-8" style={{ borderBottom: "1px solid var(--border)" }}>
              <h1
                className="text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--foreground)" }}
              >
                {doc.title}
              </h1>
              {doc.intro && (
                <div
                  className="3xl:text-lg mt-5 text-base leading-relaxed"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {doc.intro}
                </div>
              )}
            </header>

            {/* Mobile ToC */}
            <details
              className="mb-8 overflow-hidden rounded-xl lg:hidden"
              style={{ background: "var(--surface-1)", border: "1px solid var(--border)" }}
            >
              <summary
                className="cursor-pointer px-5 py-3 text-sm font-medium select-none"
                style={{ color: "var(--foreground)" }}
              >
                On this page
              </summary>
              <ul className="space-y-0.5 px-3 pb-3">
                {doc.sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollToId(s.id)}
                      className="w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {s.heading}
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <div className="space-y-12">
              {doc.sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-28">
                  <h2
                    className="3xl:text-3xl mb-4 text-xl leading-snug font-semibold"
                    style={{ color: "var(--foreground)" }}
                  >
                    {section.heading}
                  </h2>
                  <div className="legal-prose">{section.content}</div>
                </section>
              ))}
            </div>

            <div
              className="mt-16 hidden pt-8 sm:flex"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <Link
                href="/legal"
                className="inline-flex shrink-0 items-center gap-2 text-sm transition-colors"
                style={{ color: "var(--muted-foreground)" }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--foreground)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)")
                }
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-4 w-4"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 8H3m0 0l4-4M3 8l4 4" />
                </svg>
                All legal documents
              </Link>
            </div>
          </motion.article>
        </div>
      </div>
    </main>
  );
}
